/**
 * @file useDesignActions.ts
 * @description Действия панели «Дизайны» с подтверждением и уведомлениями
 * (phase19.16 Р1-6а; решения 2026-10-12): «Применить ко всему сайту»,
 * «Удалить», «Создать новый» и «Обновить из текущего вида». После
 * применения, создания и обновления — уведомление с [Опубликовать] и
 * подсветка карточки; файлы, которых больше нет в медиатеке, — отдельным
 * уведомлением. Ошибки окон создания и обновления окна показывают сами
 * (действие пробрасывает их дальше).
 */

import { useBuilder } from "../../components/site-builder/builder-context";
import { useNotify } from "../../components/ui/NotifyProvider";
import { useSiteView } from "../../components/site-view/site-view-context";
import type { ApplySiteDesignResult, SiteDesignItem } from "../../lib/api-site-designs";
import { designErrorText } from "../../lib/site-view/design-errors";
import { useDesignFlash } from "./useDesignFlash";
import type { CreateNewDesignInput, UpdateDesignInput } from "./useDesigns";

export interface DesignActions {
  /** Неопубликованных правок оформления в черновике. */
  themeChanges: number;
  flashId: string | null;
  apply: (item: SiteDesignItem) => Promise<void>;
  remove: (item: SiteDesignItem) => Promise<void>;
  create: (input: CreateNewDesignInput, onSettled: () => void) => Promise<void>;
  update: (item: SiteDesignItem, input: UpdateDesignInput, onSettled: () => void) => Promise<void>;
}

export function useDesignActions(): DesignActions {
  const b = useBuilder();
  const view = useSiteView();
  const notify = useNotify();
  const { designs } = b;
  const { flashId, flash } = useDesignFlash();
  const themeChanges = view.changes?.changedParts.find((p) => p.part === "THEME")?.count ?? 0;
  const publishAction = { label: "Опубликовать", onClick: () => view.openDialog("publish") };

  function reportDropped(dropped: ApplySiteDesignResult["dropped"]) {
    if (dropped.length === 0) return;
    notify.error(`Не перенеслось — файлов больше нет в медиатеке: ${dropped.map((d) => d.label).join(", ")}.`);
  }

  async function apply(item: SiteDesignItem) {
    const ok = await b.confirm({
      title: `Применить дизайн «${item.name}» ко всему сайту?`,
      description: `Оформление, шапка и подвал в черновике заменятся на этот дизайн.${themeChanges > 0 ? ` Неопубликованные правки оформления (${themeChanges}) пропадут.` : ""} Покупатели увидят после публикации.`,
      action: "Загрузить в черновик",
    });
    if (!ok) return;
    try {
      const result = await designs.apply(item.id);
      b.setOverride(null);
      notify.success(`Дизайн «${item.name}» в черновике. Покупатели увидят его после публикации.`, { action: publishAction });
      flash(item.id);
      reportDropped(result.dropped);
    } catch (err) {
      notify.error(designErrorText(err, "Не удалось применить дизайн."));
    }
  }

  async function remove(item: SiteDesignItem) {
    const ok = await b.confirm({ title: `Удалить дизайн «${item.name}»?`, description: "Вид сайта не изменится — удалится только сохранённый образец.", action: "Удалить", danger: true });
    if (!ok) return;
    try {
      await designs.remove(item.id);
      notify.success("Дизайн удалён");
    } catch (err) {
      notify.error(designErrorText(err, "Не удалось удалить дизайн."));
    }
  }

  async function create(input: CreateNewDesignInput, onSettled: () => void) {
    const created = await designs.createNew(input, onSettled);
    b.setOverride(null);
    notify.success(`Дизайн «${created.name}» создан и открыт в черновике. Покупатели увидят его после публикации.`, { action: publishAction });
    flash(created.id);
    if (created.applied) reportDropped(created.applied.dropped);
  }

  async function update(item: SiteDesignItem, input: UpdateDesignInput, onSettled: () => void) {
    await designs.updateFromDraft(item.id, input, onSettled);
    notify.success(`Дизайн «${item.name}» обновлён`, { action: publishAction });
    flash(item.id);
  }

  return { themeChanges, flashId, apply, remove, create, update };
}
